import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Tar Fenny emot praktikanter inom filmproduktion?",
    a: "Ja, i mån av plats. Vi tar emot LIA från yrkeshögskola, praktik från universitet och APL från gymnasiets medieprogram. Ansök gärna ett par månader innan perioden startar.",
  },
  {
    q: "Vad gör man som praktikant på en filmbyrå?",
    a: "Du följer riktiga produktioner: förberedelser och packning, assistans på inspelning (ljus, ljud, andra kamera), logg och grovklipp i post, samt versionering till sociala format. Du får eget ansvar för minst en mindre leverans under perioden.",
  },
  {
    q: "Är praktiken betald?",
    a: "LIA och skolförlagd praktik följer skolans regelverk och är normalt obetald. Vi står för resor i tjänsten under praktikperioden och för utrustning du behöver på plats.",
  },
  {
    q: "Hur lång är en praktikperiod?",
    a: "Vanligast är 8–20 veckor beroende på utbildning. Kortare perioder på 2–4 veckor för gymnasiets APL går också att lösa.",
  },
  {
    q: "Vilka orter gäller?",
    a: "Stockholm, Göteborg och Malmö. Inspelningar sker i hela Sverige och du följer med när schemat tillåter.",
  },
  {
    q: "Hur ansöker jag om praktik?",
    a: "Skicka via kontaktformuläret: utbildning, praktikperiodens datum, vad du vill lära dig och länk till det du gjort hittills. Har du inget färdigt material duger ett par råklipp eller stillbilder.",
  },
];

export const Route = createFileRoute("/praktik")({
  head: () => ({
    meta: [
      { title: "Praktik inom filmproduktion — LIA och APL hos Fenny" },
      {
        name: "description",
        content:
          "Praktik och LIA inom filmproduktion i Stockholm, Göteborg och Malmö. Vad du gör som praktikant hos Fenny, periodlängder och hur du ansöker.",
      },
      { property: "og:title", content: "Praktik inom filmproduktion — Fenny" },
      {
        property: "og:description",
        content:
          "LIA, APL och praktik på en filmbyrå med hela produktionskedjan inhouse.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/praktik" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/praktik" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/praktik", name: "Praktik inom filmproduktion — LIA och APL hos Fenny", description: "Praktik och LIA inom filmproduktion i Stockholm, Göteborg och Malmö. Vad du gör som praktikant hos Fenny, periodlängder och hur du ansöker."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Jobb", path: "/jobb" },
            { name: "Praktik", path: "/praktik" },
          ]),
        ),
      },
    ],
  }),
  component: PraktikPage,
});

function PraktikPage() {
  return (
    <LandingPage
      eyebrow="Praktik"
      title={<>Praktik inom filmproduktion — på riktiga uppdrag.</>}
      lede="Fenny tar emot praktikanter och LIA-studenter i Stockholm, Göteborg och Malmö. Du följer skarpa produktioner från brief till leverans, assisterar på inspelning och får eget ansvar för minst en leverans under perioden. Ingen kaffehämtning som huvudsyssla."
      tldr={
        <p>
          Vi tar emot <strong>LIA, universitetspraktik och gymnasie-APL</strong>{" "}
          inom filmproduktion i mån av plats. Perioder på 2–20 veckor. Ansök ett
          par månader innan start med utbildning, datum och exempel på vad du
          gjort.
        </p>
      }
      ctaPrimary={{ label: "Ansök om praktik", to: "/kontakt" }}
      ctaSecondary={{ label: "Se lediga roller", to: "/jobb" }}
      trackingId="praktik"
      bullets={[
        { title: "Skarpa produktioner", body: "Du är med på riktiga kunduppdrag, inte övningsprojekt." },
        { title: "Eget ansvar", body: "Minst en egen leverans under perioden — med handledning." },
        { title: "Hela kedjan", body: "Pre-production, inspelning och post i samma hus." },
      ]}
      process={{
        heading: "Så ser en praktikperiod ut",
        steps: [
          { label: "Introduktion", body: "Rutiner, utrustning, filstruktur och hur vi arbetar med kund.", duration: "vecka 1" },
          { label: "Assistans på set", body: "Ljus, ljud, andra kamera och riggning tillsammans med teamet.", duration: "vecka 2–6" },
          { label: "Post-production", body: "Logg, grovklipp, versionering och textning under handledning.", duration: "vecka 4–12" },
          { label: "Egen leverans", body: "Du driver en mindre produktion eller en klippleverans själv.", duration: "sista veckorna" },
        ],
      }}
      sections={[
        {
          id: "vem",
          heading: "Vem praktiken passar",
          body: (
            <>
              <p>
                Yrkeshögskolestudenter inom film och rörlig bild, universitets-
                och högskolestudenter inom medieproduktion, samt elever på
                gymnasiets medieprogram. Vi förväntar oss nyfikenhet och
                ordning — teknisk erfarenhet lär du dig här.
              </p>
              <p>
                Söker du anställning i stället för praktik, läs om{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/jobb">
                  jobb inom film hos oss
                </a>
                .
              </p>
            </>
          ),
        },
        {
          id: "handledning",
          heading: "Handledning och avstämning",
          body: (
            <p>
              Du får en namngiven handledare i teamet och veckovisa
              avstämningar. Vi fyller i skolans dokumentation och deltar i
              trepartssamtal när utbildningen kräver det.
            </p>
          ),
        },
        {
          id: "ansokan",
          heading: "Så ansöker du",
          body: (
            <p>
              Mejla via{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kontakt">
                kontaktformuläret
              </a>
              : utbildning, exakta datum för perioden, ort och vad du vill lära
              dig. Bifoga länk till det du gjort — råklipp och skolprojekt
              räcker gott.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      closing={{
        title: "Vill du praktisera hos oss?",
        body: "Skicka utbildning, period och exempel på vad du gjort så återkommer vi.",
        primary: { label: "Ansök om praktik", to: "/kontakt" },
        secondary: { label: "Läs om jobb", to: "/jobb" },
      }}
    />
  );
}
